import { Badge, Flex, Progress, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"

import Button from "../../../../../../Components/Button"
import DeadlinePill from "../../../../../../Components/DeadlinePill"
import InfoTooltip from "../../../../../../Components/InfoTooltip/InfoTooltip"
import { titleSortFilters } from "../../../../../../Components/titleSortFilters"
import Icon from "../../../../../../Icon"
import utils, { formatCurrency, paymentProcessorMap } from "../../../../../../Utils/utils"
import {
  COLUMN_ICONS,
  COUNTRY_NAMES,
  FLAG_REASON_DESCRIPTIONS,
  FLAG_REASON_META,
  ISSUER_ICON,
  LIFECYCLE_EXPLANATION,
  processorImage,
  SORTABLE,
  STAGE_DESCRIPTIONS,
  STAGE_LABELS,
  STAGE_TAG_COLORS,
  stageOf,
  transactionUrl
} from "../../constants/allPageData"
import {
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  STATUS_TAG_COLORS,
  TYPE_LABELS
} from "../../constants/statusMeta"
import AssigneeCell from "../AssigneeCell/AssigneeCell"

const { Text, Link } = Typography

// The /recover/all table's column definitions, lifted out of the page so the page file
// is about state and this one is about presentation. Both halves stay memoised at the
// call site, with the dependency lists they had inline.

// Every header shows its column icon + label, and the sort icon when the
// backend supports ordering on it (antd passes the live sort state to a title
// function). Mirrors the Alerts table headers.
const makeHeader = (t) => (label, key) => (state) =>
  titleSortFilters({
    title: t(label),
    icon: COLUMN_ICONS[key],
    sorter: SORTABLE.has(key),
    key,
    stateData: state
  })

// Generic fallback for any column not explicitly defined above.
const makeDefaultColumn = (t) => (c) => ({
  title: t(c.label),
  dataIndex: c.field_id,
  key: c.field_id,
  width: 140,
  render: (v) =>
    v == null || v === "" ? <Text type="secondary">·</Text> : <Text>{String(v)}</Text>
})

// field_id -> antd column definition. Drives the toggleable/orderable columns.
export const buildFieldDefs = ({ t, members, labelForCategory, onAssign }) => {
  const header = makeHeader(t)

  return {
    status: {
      title: header("Status", "status"),
      dataIndex: "status",
      key: "status",
      width: 180,
      fixed: "left",
      sorter: SORTABLE.has("status"),
      // Status is "what action is needed". the network lifecycle stage
      // (pre-arbitration / arbitration) lives in its own Stage column now.
      render: (v) => (
        <Tooltip title={STATUS_DESCRIPTIONS[v] ? t(STATUS_DESCRIPTIONS[v]) : ""}>
          <span className="dispute-status-tag">
            {/* 'default' is an antd Badge STATUS (themed grey dot), not a valid
                color; passing it as a color renders an invisible dot. */}
            {(STATUS_TAG_COLORS[v] || "default") === "default" ? (
              <Badge status="default" />
            ) : (
              <Badge color={STATUS_TAG_COLORS[v]} />
            )}
            <Text strong>{t(STATUS_LABELS[v] || v)}</Text>
          </span>
        </Tooltip>
      )
    },
    dispute_type: {
      title: header("Type", "dispute_type"),
      dataIndex: "dispute_type",
      key: "dispute_type",
      width: 120,
      // Case type: a formal chargeback vs a pre-dispute inquiry. Plain text like
      // Category; the value set is closed (dispute | inquiry).
      render: (v) => <Text>{t(TYPE_LABELS[v] || TYPE_LABELS.dispute)}</Text>
    },
    enrichment: {
      title: header("Enrichment", "enrichment"),
      dataIndex: "enrichment",
      key: "enrichment",
      width: 160,
      // The leading sparkles icon hardcodes a blue FILL in its SVG; neutralise it in the
      // header (see styles.less) so it reads like every other column icon, not a link.
      onHeaderCell: () => ({ className: "disputes-enrich-head" }),
      // How complete the evidence PDF is, a % derived from the evidence sections
      // (same basis as the detail-page Enrichment block). Not backend-sortable, so no
      // sorter. Muted for resolved rows and any row without an enrichment score yet.
      render: (v, row) => {
        // Shown for EVERY status, Skipped included: the score stays useful after the fact
        // when reviewing what was (or was not) submitted.
        if (v == null) {
          return <Text type="secondary">·</Text>
        }
        const pct = Math.max(0, Math.min(100, Math.round(v)))
        // Battery gauge: a compact bar coloured by level (semantic error/warning/success
        // tokens, the same vivid accents in light + dark), red < 34, orange 34-66, green
        // >= 67. The brand-blue default read as a link/primary; this reads as a status.
        const stroke = pct >= 67 ? "#16895A" : pct >= 34 ? "#DF7009" : "#D71313"
        return (
          <Tooltip
            title={
              pct >= 100
                ? t("Fully enriched. Every expected section is complete.")
                : row.enrichment_gaps > 0
                  ? row.enrichment_gaps === 1
                    ? t("_pct_% enriched. 1 section still needs detail. Open to add it.", {
                        pct
                      })
                    : t("_pct_% enriched. _count_ sections still need detail. Open to add them.", {
                        pct,
                        count: row.enrichment_gaps
                      })
                  : t("_pct_% enriched. The rest depends on data not on this transaction.", {
                      pct
                    })
            }
          >
            <span className="dispute-enrichment-cell">
              <Progress
                percent={pct}
                showInfo={false}
                strokeColor={stroke}
                className="dispute-enrich-bar"
              />
              <Text className="dispute-enrichment-pct">{pct}%</Text>
            </span>
          </Tooltip>
        )
      }
    },
    cycle_stage: {
      // Network lifecycle stage, separate from Status. The header carries our own
      // InfoTooltip (not antd's InfoCircleOutlined) explaining the lifecycle, so it
      // matches the info icons used elsewhere in the product.
      title: (state) => (
        <Flex align="center" gap={4}>
          {titleSortFilters({
            title: t("Stage"),
            icon: COLUMN_ICONS.cycle_stage,
            sorter: SORTABLE.has("cycle_stage"),
            key: "cycle_stage",
            stateData: state
          })}
          <InfoTooltip title={t(LIFECYCLE_EXPLANATION)} />
        </Flex>
      ),
      dataIndex: "cycle_stage",
      key: "cycle_stage",
      width: 150,
      render: (v, row) => {
        const stage = stageOf(v)
        // Some PSPs (e.g. Stripe) don't allow a merchant response in pre-arb/arbitration;
        // the network decides. Say so in the tooltip so the stage isn't misread as actionable.
        const tip = row.prearb_response_blocked
          ? t(
              "Your processor does not allow a response at this stage; the card network decides the outcome."
            )
          : t(STAGE_DESCRIPTIONS[stage])
        return (
          <Tooltip title={tip}>
            {stage === "first_cycle" ? (
              <Text type="secondary">{t(STAGE_LABELS.first_cycle)}</Text>
            ) : (
              <Tag className="dispute-tag" color={STAGE_TAG_COLORS[stage]}>
                {t(STAGE_LABELS[stage])}
              </Tag>
            )}
          </Tooltip>
        )
      }
    },
    flag_reason: {
      title: header("Flag", "flag_reason"),
      dataIndex: "flag_reason",
      key: "flag_reason",
      width: 160,
      render: (v) => {
        const meta = v ? FLAG_REASON_META[v] : null
        if (!meta) return <Text type="secondary">·</Text>
        return (
          <Tooltip title={FLAG_REASON_DESCRIPTIONS[v] ? t(FLAG_REASON_DESCRIPTIONS[v]) : ""}>
            <Tag className="dispute-tag" color={meta.color}>
              {t(meta.label)}
            </Tag>
          </Tooltip>
        )
      }
    },
    due_by: {
      title: header("Due", "due_by"),
      dataIndex: "due_by",
      key: "due_by",
      width: 190,
      sorter: SORTABLE.has("due_by"),
      // Only show a deadline while a response is still actionable. Once evidence
      // is submitted (or the dispute is decided) the date reads like a "decision
      // date" and confuses merchants, so we blank it out instead. The scheduled
      // auto-send lives in the Submission column, so Due is strictly the issuer
      // deadline: the urgency pill plus the exact date and time under it.
      render: (v, row) => {
        if (!v) {
          return <Text type="secondary">·</Text>
        }
        // Live statuses get the countdown pill. A decided dispute keeps the date (you still
        // want to know what the deadline was) but gets a label instead of a countdown,
        // since a "-12d left" pill on a won dispute reads like a bug. Skipped is treated
        // exactly like Won and Lost here: the deadline it was given is still worth seeing.
        const live = ["needs_response", "awaiting_review"].includes(row.status)
        return (
          <Flex vertical gap={2} align="flex-start">
            <Text>{dayjs.utc(v).format("DD MMM YYYY, HH:mm")}</Text>
            {live ? (
              <DeadlinePill due_by={v} />
            ) : (
              <Text type="secondary">{t("Deadline passed")}</Text>
            )}
          </Flex>
        )
      }
    },
    // Where evidence stands on its way to the issuer, distinct from "Due" (the
    // response deadline): scheduled auto-send date+time while Open, a manual-review
    // flag for awaiting_review (per its own status description: "ready for you to
    // review and approve"), and the sent date+time once it has gone out. Composite
    // (derived from status + scheduled_send_at/updated_at, no single backend field),
    // so no sorter, same as Enrichment.
    submission_status: {
      title: header("Submission", "submission_status"),
      dataIndex: "status",
      key: "submission_status",
      width: 230,
      render: (_, row) => {
        const st = row.status
        // Skipped had its scheduled send cancelled by stopping the fight; an undefended
        // loss is the same story ended worse (the deadline passed with nothing sent), so
        // both say "canceled" rather than pretending something went out.
        if (st === "skipped" || st === "lost_undefended")
          return <Text type="secondary">{t("Submission canceled")}</Text>
        if (st === "needs_response") {
          const sched = row.scheduled_send_at
            ? dayjs.utc(row.scheduled_send_at)
            : row.due_by
              ? dayjs.utc(row.due_by).subtract(1, "day")
              : null
          if (!sched) return <Text type="secondary">·</Text>
          // Two tidy lines (label, then the exact date+time) instead of one long one.
          return (
            <Text type="secondary" className="dispute-scheduled-send dispute-submission-2l">
              <span>{t("Scheduled to be sent")}</span>
              <span>{sched.format("DD MMM YYYY, HH:mm")}</span>
            </Text>
          )
        }
        if (st === "awaiting_review") {
          // Action needed: a soft badge (not a solid button), in the same orange the
          // Awaiting-review status tag uses, so the two read as one consistent signal.
          return (
            <Tag className="dispute-tag" color="orange">
              {t("Needs manual review and submission")}
            </Tag>
          )
        }
        // Escalated is inquiry-only: the inquiry response DID go out, the issuer just
        // was not satisfied by it, so it reads as sent like the other post-submission states.
        if (["pending_decision", "won", "lost_defended", "escalated"].includes(st)) {
          if (!row.updated_at) return <Text type="secondary">·</Text>
          // No dedicated "submitted at" field on the row; approximated the same way
          // the detail page's Activity log does (a day before the last update).
          const sent = dayjs(row.updated_at).subtract(1, "day")
          return <Text>{t("Sent _date_", { date: sent.format("DD MMM YYYY, HH:mm") })}</Text>
        }
        return <Text type="secondary">·</Text>
      }
    },
    // Customer name and email are separate columns now (was one stacked cell), so
    // they're distinct fields in the table and the CSV export.
    customer_full_name: {
      title: header("Customer full name", "customer_full_name"),
      dataIndex: "customer_full_name",
      key: "customer_full_name",
      width: 200,
      sorter: SORTABLE.has("customer_full_name"),
      render: (name, row) => {
        const txUrl = transactionUrl(row)
        return txUrl ? (
          // Typography.Link only supports a boolean `ellipsis` (not the {tooltip}
          // object Text takes), so wrap in a Tooltip to keep the hover-name behavior.
          <Tooltip title={name}>
            <Link
              href={txUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              ellipsis
            >
              {name || "."}
            </Link>
          </Tooltip>
        ) : (
          <Text ellipsis={{ tooltip: name }}>{name || "."}</Text>
        )
      }
    },
    customer_email: {
      title: header("Customer email", "customer_email"),
      dataIndex: "customer_email",
      key: "customer_email",
      width: 230,
      render: (email) => {
        if (!email) return <Text type="secondary">·</Text>
        return (
          <span onClick={(e) => e.stopPropagation()} className="disputes-copy-cell">
            <Text
              type="secondary"
              ellipsis={{ tooltip: email }}
              copyable={{
                text: email,
                tooltips: [t("Copy"), t("Copied")],
                icon: [<Icon key="c" type="copy" />, <Icon key="d" type="check_mark" />]
              }}
            >
              {email}
            </Text>
          </span>
        )
      }
    },
    assignee: {
      title: header("Assignee", "assignee"),
      dataIndex: "assigned_to",
      key: "assignee",
      width: 180,
      render: (_, row) => (
        <AssigneeCell
          row={row}
          members={members}
          t={t}
          onAssign={(member) => onAssign({ id: row.id, member, prev: row.assigned_to || null })}
        />
      )
    },
    // Category and Reason code are separate columns now (was one stacked cell).
    reason_category: {
      title: header("Category", "reason_category"),
      dataIndex: "reason_category",
      key: "reason_category",
      width: 190,
      render: (v, row) => <Text>{row.reason_category_label || labelForCategory(v)}</Text>
    },
    reason_code: {
      title: header("Reason code", "reason_code"),
      dataIndex: "reason_code",
      key: "reason_code",
      width: 150,
      render: (v, row) => {
        if (!v) return <Text type="secondary">·</Text>
        return (
          <Flex align="center" gap={4}>
            <Text style={{ fontVariantNumeric: "tabular-nums" }}>{v}</Text>
            {row.reason_code_label ? <InfoTooltip title={row.reason_code_label} /> : null}
          </Flex>
        )
      }
    },
    issuer: {
      title: header("Card network", "issuer"),
      dataIndex: "issuer",
      key: "issuer",
      width: 140,
      render: (v) => {
        if (!v) return <Text type="secondary">·</Text>
        const label = v.charAt(0).toUpperCase() + v.slice(1)
        const icon = ISSUER_ICON[v]
        return (
          <Flex align="center" gap={8}>
            {icon ? <Icon type={icon} className="disputes-brand-icon" /> : null}
            <Text>{label}</Text>
          </Flex>
        )
      }
    },
    issuing_bank: {
      title: header("Issuer", "issuing_bank"),
      dataIndex: "issuing_bank",
      key: "issuing_bank",
      width: 180,
      sorter: SORTABLE.has("issuing_bank"),
      render: (v) => (v ? <Text>{v}</Text> : <Text type="secondary">·</Text>)
    },
    card_type: {
      title: header("Card type", "card_type"),
      dataIndex: "card_type",
      key: "card_type",
      width: 120,
      render: (v) => (v ? v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : ".")
    },
    transaction_date: {
      title: header("Transaction date", "transaction_date"),
      dataIndex: "transaction_date",
      key: "transaction_date",
      width: 160,
      sorter: SORTABLE.has("transaction_date"),
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : ".")
    },
    dispute_created_at: {
      title: header("Dispute date", "dispute_created_at"),
      dataIndex: "dispute_created_at",
      key: "dispute_created_at",
      width: 150,
      sorter: SORTABLE.has("dispute_created_at"),
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : ".")
    },
    updated_at: {
      title: header("Last update", "updated_at"),
      dataIndex: "updated_at",
      key: "updated_at",
      width: 170,
      sorter: SORTABLE.has("updated_at"),
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : ".")
    },
    amount: {
      title: header("Amount", "amount"),
      dataIndex: "amount",
      key: "amount",
      width: 130,
      sorter: SORTABLE.has("amount"),
      render: (v) => formatCurrency(Number(v) || 0)
    },
    processor: {
      title: header("Payment processor", "processor"),
      dataIndex: "processor",
      key: "processor",
      width: 170,
      render: (v) => {
        if (!v) return <Text type="secondary">·</Text>
        const label = paymentProcessorMap[v] || v.charAt(0).toUpperCase() + v.slice(1)
        const src = processorImage(v)
        return (
          <Flex align="center" gap={8}>
            {src ? (
              <img
                src={src}
                onError={utils.addDefaultSrc}
                alt={label}
                width={20}
                height={20}
                className="disputes-brand-icon"
              />
            ) : null}
            <Text>{label}</Text>
          </Flex>
        )
      }
    },
    country: {
      title: header("Country", "country"),
      dataIndex: "country",
      key: "country",
      width: 110,
      render: (v) => (v ? COUNTRY_NAMES[v] || v : <Text type="secondary">·</Text>)
    },
    card_last4: {
      title: header("Card last 4", "card_last4"),
      dataIndex: "card_last4",
      key: "card_last4",
      width: 140,
      // Digits render as a masked card; non-digits are a funding source (Link,
      // Apple Pay, ...) for payments that carry no card number.
      render: (v) => {
        if (!v) return <Text type="secondary">·</Text>
        return /^\d{3,4}$/.test(v) ? (
          <Text style={{ fontVariantNumeric: "tabular-nums" }}>•••• {v}</Text>
        ) : (
          <Text>{v}</Text>
        )
      }
    },
    currency: {
      title: header("Currency", "currency"),
      dataIndex: "currency",
      key: "currency",
      width: 110,
      render: (v) => v || "."
    },
    // The processor's own dispute reference (e.g. du_… for Stripe). Now an
    // optional column since the # column shows a clean row number instead.
    processor_dispute_id: {
      title: header("Dispute ID", "processor_dispute_id"),
      dataIndex: "processor_dispute_id",
      key: "processor_dispute_id",
      width: 180,
      render: (v, row) => {
        if (!v) return <Text type="secondary">·</Text>
        const txUrl = transactionUrl(row)
        const procLabel =
          paymentProcessorMap[row.processor] ||
          (row.processor
            ? row.processor.charAt(0).toUpperCase() + row.processor.slice(1)
            : t("your processor"))
        // Link only when the processor exposes a deep-link (Stripe always; others
        // when the backend resolved a transaction_url). Otherwise plain text with a
        // tooltip, so it's clear why some IDs link and others don't.
        return txUrl ? (
          <Tooltip title={t("Open this transaction in _processor_", { processor: procLabel })}>
            <Link
              href={txUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {v}
            </Link>
          </Tooltip>
        ) : (
          <Tooltip
            title={t(
              "_processor_ does not provide a direct link to this dispute by ID. Open it from your _processor_ dashboard.",
              { processor: procLabel }
            )}
          >
            <Text style={{ fontVariantNumeric: "tabular-nums" }}>{v}</Text>
          </Tooltip>
        )
      }
    }
  }
}

// The final antd `columns` array: the always-on pin column, the optional # column
// (both frozen left), then the merchant's active data columns in their saved order.
export const buildColumns = ({ t, tableColumns, fieldDefs, onPin }) => {
  const getDefaultColumn = makeDefaultColumn(t)

  const pinCol = {
    key: "pin",
    dataIndex: "pinned",
    width: 64,
    fixed: "left",
    title: <InfoTooltip title={t("Pin a dispute to keep it pinned to the top of the list.")} />,
    render: (_, row) => (
      <Tooltip title={row.pinned ? t("Unpin") : t("Pin to top")}>
        <Button
          type="text"
          shape="circle"
          className="disputes-pin-btn"
          aria-label={row.pinned ? t("Unpin") : t("Pin to top")}
          icon={<Icon type={row.pinned ? "pin_active" : "pin"} />}
          onClick={(e) => {
            e.stopPropagation()
            onPin(row)
          }}
        />
      </Tooltip>
    )
  }
  // #: the dispute's real per-account sequence number. Now a toggleable column
  // (hide it from Edit columns); when shown it stays pinned at the left, next to
  // the pin column, so the fixed-left set stays contiguous.
  const idCol = {
    key: "number",
    dataIndex: "number",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: null,
        icon: "hashtag",
        sorter: true,
        key: "number",
        stateData: state
      }),
    width: 64,
    fixed: "left",
    render: (value) => <Text style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
  }
  const active = (tableColumns || []).filter((c) => c.active)
  const numberActive = active.some((c) => c.field_id === "number")
  // '#' is rendered as the fixed idCol above; skip it in the data-column map so
  // it isn't duplicated.
  const dataCols = active
    .filter((c) => c.field_id !== "number")
    .map((c) => fieldDefs[c.field_id] || getDefaultColumn(c))
  // Keep fixed-left data columns (Status) contiguous with the pin/# freeze group
  // regardless of the saved Edit-columns order. If the user drags Status below a
  // scrollable column, antd would otherwise detach it from the left freeze group,
  // so we force any `fixed: 'left'` entry to the front of the data columns. Stable
  // (Array.prototype.sort is stable) so the rest keep their saved order.
  const orderedDataCols = [...dataCols].sort(
    (a, b) => (b.fixed === "left" ? 1 : 0) - (a.fixed === "left" ? 1 : 0)
  )
  return [pinCol, ...(numberActive ? [idCol] : []), ...orderedDataCols]
}
